import React, { useState, useEffect, useRef } from "react";
import { Box, Snackbar, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import Table from "../Table";
import AccessibleTooltip from "../Tooltip/Tooltip.js";
import SubHeader from "../SubHeader";
import CustomerFilter from "./CustomerFilter.js";

import EditIcon from "../../assets/images/editIcon.svg";
import DeleteIcon from "../../assets/images/DeleteIcon.svg";

import AddEditCustomer from "./AddEditCustomer";
import DeleteModal from "./DeleteModal";

import { deleteCustomer } from "../../Actions/CustomerManagementAction";

import {
  getCustomerAccess,
  processCustomerRows,
  triggerToast,
  getCustomerColumns,
  fetchCustomers,
} from "../../utils/customerUtils.js";

import "./mustyle.scss";

/* ----------------------------------
 * Loader + Grid Wrapper
 * ---------------------------------- */
const GridWithLoader = ({ loading, rows, columns, setHasData, onSortChange, page, setPage, totalPages }) =>
  loading && rows.length === 0 ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <Box sx={{ height: 400, width: "100%" }}>
      <Table columns={columns} rows={rows} setHasData={setHasData} page={page} onPageChange={setPage} totalPages={totalPages}  onSortChange={onSortChange}/>
    </Box>
  );

/* ----------------------------------
 * Customer Component
 * ---------------------------------- */
const Customer = () => {
  const dispatch = useDispatch();

  /* ---------- Local state ---------- */
  const [open, setOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [hasData, setHasData] = useState(true);
  const [actionButton, setActionButton] = useState("");
  const [editData, setEditData] = useState(null);
  const [selectedEditId, setSelectedEditid] = useState();
  const [deviceIdToDelete, setDeviceIdToDelete] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("ASC");

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  /* ---------- Access control ---------- */
  const token = JSON.parse(localStorage.getItem("token"));
  const { create, edit, delete: canDelete } = getCustomerAccess(token);

  /* ---------- Redux state ---------- */
  const { dashboardData, loading, toastMessage, toastOpen } = useSelector(
    (state) => state.customerData,
  );

  /* ---------- Table setup ---------- */
  const handleEdit = (deviceData, selectedId) => {
    setSelectedEditid(selectedId);
    setEditData(deviceData.row);
    setActionButton("edit");
    setOpen(true);
  };
  const handleSort = (field) => {
  if (sortBy === field) {
    setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC"); // toggle
  } else {
    setSortBy(field);
    setSortDirection("ASC"); // default
  }
  setPage(1); // reset page when sorting
};


  const columns = getCustomerColumns(
    edit,
    canDelete,
    handleEdit,
    setOpenDeleteModal,
    setDeviceIdToDelete,
    EditIcon,
    DeleteIcon,
    AccessibleTooltip,
    handleSort
  );

  const processedRows = processCustomerRows(dashboardData.content);

  /* ----------------------------------
   * Fetch customers (single source)
   * ---------------------------------- */
  useEffect(() => {
  fetchCustomers(dispatch, page, {
    size: 20,
    sortBy,
    sortDirection,
    searchTerm,
    statuses: [],
  });
}, [dispatch, page, searchTerm, sortBy, sortDirection]);
useEffect(() =>{
  setTotalPages(dashboardData?.totalPages)
},[dashboardData])


  /* ---------- Delete ---------- */
  const handleDelete = (id) => {
    dispatch(deleteCustomer(id));
    triggerToast(dispatch, "Deleted Successfully");
  };

  /* ---------- Toast ---------- */
  const handleToastClose = () => {
    triggerToast(dispatch, "", false);
  };

  /* ---------- Accessibility ---------- */
  const createBtnRef = useRef(null);
  useEffect(() => {
    createBtnRef.current?.focus();
  }, []);

  return (
    <>
      {/* Screen reader status */}
      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {!hasData
          ? "No data available"
          : `${processedRows.length} item${
              processedRows.length > 1 ? "s" : ""
            } found`}
      </span>

      <main
        className="main_ele_wrapper userMangement_wrapper"
        role="main"
        aria-label="Customer Management Section"
      >
        {/* ---------- Header ---------- */}
        <SubHeader
          heading="Customer Management"
          headerButton={
            create && (
              <div className="AddUser new-entry">
                <button
                  ref={createBtnRef}
                  className="addDevice"
                  onClick={() => {
                    setOpen(true);
                    setActionButton("add");
                  }}
                  aria-label="Create new customer"
                >
                  Create Customer
                </button>
              </div>
            )
          }
        />

        {/* ---------- Filters ---------- */}
        <CustomerFilter
          onApply={({ searchTerm }) => {
            setSearchTerm(searchTerm);
            setPage(1);
          }}
          onClear={() => {
            setSearchTerm("");
            setPage(1);
          }}
        />

        {/* ---------- Table ---------- */}
        <div className="inner_Wrapper">
          <div className="floorMangementTable_wrapper manageUserTable_wrapper">
            <GridWithLoader
              loading={loading}
              rows={processedRows}
              columns={columns}
              setHasData={setHasData}
              page={page}
              setPage={setPage}
              totalPages={totalPages} 
              onSortChange={({ field, order }) => {
                setSortBy(field);
                setSortDirection(order.toUpperCase());
                setPage(1);
              }}

            />
          </div>
        </div>
      </main>

      {/* ---------- Add / Edit ---------- */}
      <AddEditCustomer
        open={open}
        setOpen={setOpen}
        actionButton={actionButton}
        setActionButton={setActionButton}
        editData={editData}
        handleClose={() => {
          setTimeout(() => {
            document.getElementById(selectedEditId)?.focus();
          }, 200);
        }}
      />

      <DeleteModal
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDelete={() => handleDelete(deviceIdToDelete)}
      />

      <Snackbar
        open={toastOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        message={toastMessage}
        onClose={handleToastClose}
        className="success"
      />
    </>
  );
};

export default Customer;
